import { useMemo } from 'react';
import styled from 'styled-components';
import { IntegrationMessageDTO } from '../../../api/api';
import { CloseIcon } from '../../../assets/icons/CloseIcon';
import Button from '../../../components/button/Button';
import DialogView from '../../../components/dialog-view/DialogView';
import IconButton from '../../../components/icon-button/IconButton';
import Typography from '../../../components/typography/Typography';
import NotificationService from '../../../services/NotificationService';
import { formatDateWithTime } from '../../../utils/dateHandling';
import { formatJson } from '../../../utils/jsonFormatting';
import { log } from '../../../utils/logging/log';
import { MessageBox, getMessageStatus, shortMessageType } from './useIntegrationMessagesList';

interface Props {
  message: IntegrationMessageDTO;
  box: MessageBox;
  handleClose: () => void;
}

const IntegrationMessageDialog = (props: Props) => {
  const { message, box, handleClose } = props;

  const formattedPayload = useMemo(() => formatJson(message.payload), [message.payload]);

  const handleCopy = () => {
    navigator.clipboard
      .writeText(formattedPayload)
      .then(() => NotificationService.success('Payload kopieret'))
      .catch((err) => {
        log(err);
        NotificationService.error('Kunne ikke kopiere payload');
      });
  };

  return (
    <DialogView handleClose={handleClose}>
      <Container>
        <Header>
          <div>
            <Typography variant="h4">{shortMessageType(message.messageType)}</Typography>
            <Typography variant="span" color="darkGrey">
              {box === 'inbox' ? 'Modtaget besked' : 'Afsendt besked'}
            </Typography>
          </div>
          <IconButton onClick={handleClose} aria-label="Luk">
            <CloseIcon size="20px" />
          </IconButton>
        </Header>

        <Content>
          <Details>
            <Detail label="Id" value={message.id} />
            <Detail label="Tidspunkt" value={formatDateWithTime(message.occurredOn)} />
            <Detail label="Status" value={getMessageStatus(message)} />
            <Detail label="Behandlet" value={formatDateWithTime(message.processedDate)} />
            <Detail label="Antal fejl" value={String(message.failCount ?? 0)} />
            <Detail label="Næste forsøg" value={formatDateWithTime(message.nextAttemptDate)} />
            {box === 'inbox' && <Detail label="Integration event id" value={message.integrationEventId} />}
            {box === 'inbox' && <Detail label="Correlation id" value={message.correlationId} />}
            <Detail label="Trace parent" value={message.traceParent} />
            <Detail label="Fuldt typenavn" value={message.messageType} wide />
          </Details>

          {message.errorMessage && (
            <>
              <SectionHeader>
                <Typography variant="h6">Fejlbesked</Typography>
              </SectionHeader>
              <ErrorBox>{message.errorMessage}</ErrorBox>
            </>
          )}

          <SectionHeader>
            <Typography variant="h6">Payload</Typography>
            <Button variant="tertiary" compact onClick={handleCopy}>
              Kopiér
            </Button>
          </SectionHeader>
          <Payload>{formattedPayload || '-'}</Payload>
        </Content>

        <Footer>
          <Button variant="secondary" onClick={handleClose}>
            Luk
          </Button>
        </Footer>
      </Container>
    </DialogView>
  );
};

interface DetailProps {
  label: string;
  value?: string;
  wide?: boolean;
}

const Detail = (props: DetailProps) => {
  const { label, value, wide } = props;

  return (
    <DetailContainer wide={wide}>
      <Typography variant="span" color="darkGrey">
        {label}
      </Typography>
      <DetailValue>{value || '-'}</DetailValue>
    </DetailContainer>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;

  box-sizing: border-box;
  width: 900px;
  max-width: 92vw;
  max-height: 88vh;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  column-gap: ${(props) => props.theme.spacing(4)};

  padding: ${(props) => props.theme.spacing(6)};
  border-bottom: 1px solid ${(props) => props.theme.palette.grey.black10};
`;

const Content = styled.div`
  overflow-y: auto;
  padding: ${(props) => props.theme.spacing(6)};
`;

const Details = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${(props) => props.theme.spacing(4)};
`;

const DetailContainer = styled.div<{ wide?: boolean }>`
  display: flex;
  flex-direction: column;
  min-width: 0;
  grid-column: ${(props) => (props.wide ? '1 / -1' : 'auto')};
`;

const DetailValue = styled.span`
  overflow-wrap: anywhere;
  font-family: 'Courier New', Courier, monospace;
  font-size: 12px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  margin-top: ${(props) => props.theme.spacing(6)};
  margin-bottom: ${(props) => props.theme.spacing(2)};
`;

const ErrorBox = styled.pre`
  margin: 0;
  padding: ${(props) => props.theme.spacing(4)};

  border-radius: 8px;
  background: ${(props) => props.theme.palette.functions.error.tertiary};
  color: ${(props) => props.theme.palette.functions.error.primary};

  font-family: 'Courier New', Courier, monospace;
  font-size: 12px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const Payload = styled.pre`
  margin: 0;
  padding: ${(props) => props.theme.spacing(4)};

  max-height: 45vh;
  overflow: auto;

  border-radius: 8px;
  border: 1px solid ${(props) => props.theme.palette.grey.black10};
  background: ${(props) => props.theme.palette.grey.black5};

  font-family: 'Courier New', Courier, monospace;
  font-size: 12px;
  line-height: 1.5;
  tab-size: 2;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;

  padding: ${(props) => props.theme.spacing(4)} ${(props) => props.theme.spacing(6)};
  border-top: 1px solid ${(props) => props.theme.palette.grey.black10};
`;

export default IntegrationMessageDialog;
